/**
 * ModulesStrip — the Portfolio Kit curriculum section (Phase C).
 *
 * Renders the 4 Portfolio Kit modules as a responsive card grid inside the
 * logged-in workspace. Each card links to /portfolio-kit/modules/:slug.
 */
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { PORTFOLIO_MODULES } from "../../data/portfolioKitModules";
import { L } from "../../utils/localized";

export default function ModulesStrip() {
  const theme = useTheme();
  const { t, i18n } = useTranslation("portfolio-kit");
  const portfolioModules = L(PORTFOLIO_MODULES, i18n.language);

  return (
    <Box component="section" aria-labelledby="portfolio-modules-heading">
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography
          id="portfolio-modules-heading"
          variant="h5"
          sx={{ fontWeight: 500 }}
        >
          {t("ModulesStrip.heading")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680 }}>
          {t("ModulesStrip.subheading")}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
          },
        }}
      >
        {portfolioModules.map((mod) => (
          <Card
            key={mod.slug}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: alpha(theme.palette.text.primary, 0.12),
              transition: theme.transitions.create(["border-color", "box-shadow"], {
                duration: theme.transitions.duration.shortest,
              }),
              "&:hover": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            <CardActionArea
              component={RouterLink}
              to={`/portfolio-kit/modules/${mod.slug}`}
              sx={{ p: 2.5, height: "100%", alignItems: "flex-start" }}
            >
              <Stack spacing={1.25} sx={{ height: "100%" }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Chip
                    label={mod.number}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.background.paper,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t("ModulesStrip.readMinutes", { minutes: mod.readMinutes })}
                  </Typography>
                </Stack>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                  {mod.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.55, flexGrow: 1 }}
                >
                  {mod.tagline}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="button"
                    sx={{ textTransform: "none", color: theme.palette.primary.main, fontWeight: 500 }}
                  >
                    {t("ModulesStrip.readModule")}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </Stack>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
